import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Tabs,
  Tab,
  Tooltip as MuiTooltip,
  IconButton,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Place as PlaceIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  BarChart as BarChartIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  MenuBook as PlaceNameIcon,
  FormatListNumbered as ListIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  FileDownload as ExportIcon,
  TableChart as CsvIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  DataObject as DataIcon,
} from '@mui/icons-material';
import {
  exportHourlyDataToCSV,
  exportWeeklyDataToCSV,
  exportDwellDataToCSV,
  exportHeatmapToCSV,
  exportAllChartDataToCSV,
  exportElementAsPNG,
  exportElementAsPDF,
  exportChartsAsPDFReport,
  HourlyData,
  WeeklyData,
  DwellData,
  HeatmapCell,
} from '../../utils/chartExport';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { PathfinderDataPoint, CoTravelerResult } from '../../services/pathfinderApi';
import { generateDeviceColor } from '../../theme/entityColors';

// Filter types for chart-based filtering
export interface ChartFilter {
  type: 'hour' | 'day' | 'hourDay';
  hour?: number;      // 0-23
  day?: string;       // 'Sun', 'Mon', etc.
  dayIndex?: number;  // 0-6 (Sunday = 0)
}

interface InteractiveChartsProps {
  dataPoints: PathfinderDataPoint[];
  dwellLocations: any[];
  coTravelerResults: CoTravelerResult[];
  studentColors: Record<string, string>;
  onFilterChange?: (filter: ChartFilter | null) => void;
  activeFilter?: ChartFilter | null;
  // Place Names enrichment
  enablePlaceNames?: boolean;
  setEnablePlaceNames?: (enabled: boolean) => void;
  isEnrichingSites?: boolean;
  // Dwell location click - zoom to location on map
  onDwellClick?: (lat: number, lon: number, locationName: string) => void;
}

const InteractiveCharts: React.FC<InteractiveChartsProps> = ({
  dataPoints,
  dwellLocations,
  coTravelerResults,
  studentColors,
  onFilterChange,
  activeFilter,
  enablePlaceNames = false,
  setEnablePlaceNames,
  isEnrichingSites = false,
  onDwellClick,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [dwellLimit, setDwellLimit] = useState(25);
  const [dwellLimitAnchor, setDwellLimitAnchor] = useState<null | HTMLElement>(null);
  const [dwellPage, setDwellPage] = useState(0);
  const DWELLS_PER_PAGE = 15;
  
  // Export state
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Refs for chart containers
  const activityChartsRef = useRef<HTMLDivElement>(null);
  const locationsChartsRef = useRef<HTMLDivElement>(null);
  const heatmapRef = useRef<HTMLDivElement>(null);
  const hourlyChartRef = useRef<HTMLDivElement>(null);
  const weeklyChartRef = useRef<HTMLDivElement>(null);
  const dwellChartRef = useRef<HTMLDivElement>(null);
  
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Handle chart click to set filter
  const handleHourClick = useCallback((hour: number) => {
    if (activeFilter?.type === 'hour' && activeFilter.hour === hour) {
      // Clear filter if clicking same hour
      onFilterChange?.(null);
    } else {
      onFilterChange?.({ type: 'hour', hour });
    }
  }, [activeFilter, onFilterChange]);

  const handleDayClick = useCallback((day: string, dayIndex: number) => {
    if (activeFilter?.type === 'day' && activeFilter.day === day) {
      // Clear filter if clicking same day
      onFilterChange?.(null);
    } else {
      onFilterChange?.({ type: 'day', day, dayIndex });
    }
  }, [activeFilter, onFilterChange]);

  const handleHeatmapCellClick = useCallback((hour: number, day: string, dayIndex: number) => {
    if (activeFilter?.type === 'hourDay' && activeFilter.hour === hour && activeFilter.day === day) {
      // Clear filter if clicking same cell
      onFilterChange?.(null);
    } else {
      onFilterChange?.({ type: 'hourDay', hour, day, dayIndex });
    }
  }, [activeFilter, onFilterChange]);

  const clearFilter = useCallback(() => {
    onFilterChange?.(null);
  }, [onFilterChange]);
  
  // Export handlers
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const showExportSuccess = (message: string) => {
    setExportSnackbar({ open: true, message, severity: 'success' });
  };

  const showExportError = (message: string) => {
    setExportSnackbar({ open: true, message, severity: 'error' });
  };

  const handleExportHeatmapCSV = () => {
    handleExportMenuClose();
    if (activityHeatmapData.cells.length > 0) {
      exportHeatmapToCSV(activityHeatmapData.cells as HeatmapCell[]);
      showExportSuccess('Heatmap data exported to CSV');
    } else {
      showExportError('No heatmap data to export');
    }
  };

  const handleExportHourlyCSV = () => {
    handleExportMenuClose();
    if (activityHeatmapData.hourlyData.length > 0) {
      exportHourlyDataToCSV(activityHeatmapData.hourlyData as HourlyData[], uniqueDeviceIds);
      showExportSuccess('Hourly activity data exported to CSV');
    } else {
      showExportError('No hourly data to export');
    }
  };

  const handleExportWeeklyCSV = () => {
    handleExportMenuClose();
    if (activityHeatmapData.weeklyData.length > 0) {
      exportWeeklyDataToCSV(activityHeatmapData.weeklyData as WeeklyData[], uniqueDeviceIds);
      showExportSuccess('Weekly pattern data exported to CSV');
    } else {
      showExportError('No weekly data to export');
    }
  };

  const handleExportDwellCSV = () => {
    handleExportMenuClose();
    if (dwellTimelineData.length > 0) {
      exportDwellDataToCSV(dwellTimelineData as DwellData[], dwellDeviceIds);
      showExportSuccess('Dwell location data exported to CSV');
    } else {
      showExportError('No dwell data to export');
    }
  };

  const handleExportAllDataCSV = () => {
    handleExportMenuClose();
    exportAllChartDataToCSV({
      hourlyData: activityHeatmapData.hourlyData as HourlyData[],
      weeklyData: activityHeatmapData.weeklyData as WeeklyData[],
      dwellData: dwellTimelineData as DwellData[],
      heatmapCells: activityHeatmapData.cells as HeatmapCell[],
      deviceIds: uniqueDeviceIds,
    });
    showExportSuccess('All chart data exported to CSV');
  };

  const handleExportCurrentTabPNG = async () => {
    handleExportMenuClose();
    setIsExporting(true);
    try {
      const ref = activeTab === 0 ? activityChartsRef : locationsChartsRef;
      if (ref.current) {
        await exportElementAsPNG(ref.current, activeTab === 0 ? 'activity_charts' : 'location_charts');
        showExportSuccess('Charts exported as PNG');
      } else {
        showExportError('No charts to export');
      }
    } catch {
      showExportError('Failed to export PNG');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCurrentTabPDF = async () => {
    handleExportMenuClose();
    setIsExporting(true);
    try {
      const ref = activeTab === 0 ? activityChartsRef : locationsChartsRef;
      const title = activeTab === 0 ? 'Activity Charts' : 'Location Charts';
      if (ref.current) {
        await exportElementAsPDF(ref.current, activeTab === 0 ? 'activity_charts' : 'location_charts', title);
        showExportSuccess('Charts exported as PDF');
      } else {
        showExportError('No charts to export');
      }
    } catch {
      showExportError('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportFullPDFReport = async () => {
    handleExportMenuClose();
    setIsExporting(true);
    try {
      const chartElements: { element: HTMLElement; title: string }[] = [];
      
      if (heatmapRef.current) {
        chartElements.push({ element: heatmapRef.current, title: 'Activity Heatmap' });
      }
      if (hourlyChartRef.current) {
        chartElements.push({ element: hourlyChartRef.current, title: 'Hourly Activity' });
      }
      if (weeklyChartRef.current) {
        chartElements.push({ element: weeklyChartRef.current, title: 'Weekly Pattern' });
      }
      if (dwellChartRef.current) {
        chartElements.push({ element: dwellChartRef.current, title: 'Dwell Locations' });
      }
      
      if (chartElements.length > 0) {
        await exportChartsAsPDFReport(chartElements, 'charts_report', 'OSINT Explorer - Charts Report');
        showExportSuccess('Full PDF report exported');
      } else {
        showExportError('No charts to export');
      }
    } catch {
      showExportError('Failed to export PDF report');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Get unique device IDs for stacked chart
  const uniqueDeviceIds = useMemo(() => {
    const devices = new Set<string>();
    dataPoints.forEach(point => {
      const deviceId = point.deviceId || point.studentId || 'Unknown';
      devices.add(deviceId);
    });
    return Array.from(devices);
  }, [dataPoints]);

  // Process track data for timeline chart - stacked by device
  const trackTimelineData = useMemo(() => {
    if (!dataPoints.length) return [];

    // Group by hour and device
    const hourlyData: Record<string, { 
      time: string; 
      timestamp: number;
      [deviceId: string]: number | string;
    }> = {};

    dataPoints.forEach(point => {
      const date = new Date(point.timestamp);
      const hourKey = date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit',
        hour12: true 
      });
      const deviceId = point.deviceId || point.studentId || 'Unknown';

      if (!hourlyData[hourKey]) {
        hourlyData[hourKey] = { 
          time: hourKey, 
          timestamp: date.getTime()
        };
        // Initialize all device counts to 0
        uniqueDeviceIds.forEach(id => {
          hourlyData[hourKey][id] = 0;
        });
      }
      
      // Increment count for this device
      hourlyData[hourKey][deviceId] = (hourlyData[hourKey][deviceId] as number || 0) + 1;
    });

    return Object.values(hourlyData)
      .sort((a, b) => (a.timestamp as number) - (b.timestamp as number))
      .slice(0, 50); // Limit to 50 points for performance
  }, [dataPoints, uniqueDeviceIds]);

  // Process dwell data for timeline chart - group by location, stack by device
  const dwellTimelineData = useMemo(() => {
    if (!dwellLocations.length) return [];

    // Group dwells by location name, tracking duration per device
    const locationMap = new Map<string, {
      location: string;
      totalDuration: number;
      visitCount: number;
      entityDurations: Map<string, number>; // deviceId -> duration
      latitude: number;
      longitude: number;
    }>();

    dwellLocations.forEach(dwell => {
      const start = new Date(dwell.start_time);
      const end = new Date(dwell.end_time);
      const duration = (end.getTime() - start.getTime()) / (1000 * 60); // minutes
      
      // Use deviceId or entity_id for device identification
      let deviceId = dwell.deviceId || dwell.device_id || dwell.entity_id || dwell.entityId || 'Unknown';
      
      // Normalize: check if studentColors has this ID, if not try without GN- prefix
      if (!studentColors[deviceId] && deviceId.startsWith('GN-')) {
        const withoutPrefix = deviceId.replace('GN-', '');
        if (studentColors[withoutPrefix]) {
          deviceId = withoutPrefix;
        }
      }
      // Also try with GN- prefix if not found
      if (!studentColors[deviceId] && !deviceId.startsWith('GN-')) {
        const withPrefix = `GN-${deviceId}`;
        if (studentColors[withPrefix]) {
          deviceId = withPrefix;
        }
      }

      // Prefer geocoded site_name, then place_name, then address, finally coordinates
      const locationName = dwell.site_name || dwell.place_name || dwell.place_address || dwell.name ||
        (dwell.latitude && dwell.longitude ? `${dwell.latitude.toFixed(5)}, ${dwell.longitude.toFixed(5)}` : 'Unknown');
      
      const existing = locationMap.get(locationName);
      if (existing) {
        existing.totalDuration += Math.round(duration);
        existing.visitCount += 1;
        existing.entityDurations.set(deviceId, (existing.entityDurations.get(deviceId) || 0) + Math.round(duration));
      } else {
        const entityDurations = new Map<string, number>();
        entityDurations.set(deviceId, Math.round(duration));
        locationMap.set(locationName, {
          location: locationName,
          totalDuration: Math.round(duration),
          visitCount: 1,
          entityDurations,
          latitude: dwell.latitude,
          longitude: dwell.longitude,
        });
      }
    });

    // Convert to array and sort by total duration
    return Array.from(locationMap.values())
      .map(loc => {
        // Build object with duration per device for stacking
        const data: Record<string, any> = {
          location: loc.location,
          totalDuration: loc.totalDuration,
          visitCount: loc.visitCount,
          visitLabel: loc.visitCount > 1 ? `×${loc.visitCount}` : '',
          entityCount: loc.entityDurations.size,
          latitude: loc.latitude,
          longitude: loc.longitude,
        };
        // Add duration for each device
        loc.entityDurations.forEach((duration, deviceId) => {
          data[deviceId] = duration;
        });
        return data;
      })
      .sort((a, b) => b.totalDuration - a.totalDuration)
      .slice(0, dwellLimit);
  }, [dwellLocations, studentColors, dwellLimit]);

  // Get unique device IDs from dwell data for stacking
  const dwellDeviceIds = useMemo(() => {
    const devices = new Set<string>();
    dwellTimelineData.forEach(loc => {
      Object.keys(loc).forEach(key => {
        if (!['location', 'totalDuration', 'visitCount', 'visitLabel', 'entityCount', 'latitude', 'longitude'].includes(key)) {
          devices.add(key);
        }
      });
    });
    return Array.from(devices);
  }, [dwellTimelineData]);

  // Paginated dwell data
  const paginatedDwellData = useMemo(() => {
    const start = dwellPage * DWELLS_PER_PAGE;
    const end = start + DWELLS_PER_PAGE;
    return dwellTimelineData.slice(start, end);
  }, [dwellTimelineData, dwellPage]);

  const totalDwellPages = Math.ceil(dwellTimelineData.length / DWELLS_PER_PAGE);

  // Reset page when limit changes
  React.useEffect(() => {
    setDwellPage(0);
  }, [dwellLimit]);

  // Process co-traveler data for timeline chart
  const coTravelerTimelineData = useMemo(() => {
    if (!coTravelerResults.length) return [];

    const encountersByHour: Record<string, { time: string; count: number; pairs: Set<string> }> = {};

    coTravelerResults.forEach(result => {
      result.encounters?.forEach(encounter => {
        const date = new Date(encounter.timestamp);
        const hourKey = date.toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit',
          hour12: true 
        });

        if (!encountersByHour[hourKey]) {
          encountersByHour[hourKey] = { time: hourKey, count: 0, pairs: new Set() };
        }
        encountersByHour[hourKey].count++;
        encountersByHour[hourKey].pairs.add(`${result.primaryDevice}-${result.coTravelerDevice}`);
      });
    });

    return Object.values(encountersByHour)
      .map(d => ({ time: d.time, encounters: d.count, pairs: d.pairs.size }))
      .slice(0, 30);
  }, [coTravelerResults]);

  // Activity Heatmap data (Hour × Day of Week) - stacked by device
  const activityHeatmapData = useMemo(() => {
    if (!dataPoints.length) return { cells: [], hourlyData: [], weeklyData: [] };

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Create a grid: rows = days, columns = hours
    // Each cell tracks count per device
    const grid: Record<string, Record<string, Record<string, number>>> = {};
    const hourlyByDevice: Record<string, Record<string, number>> = {};
    const weeklyByDevice: Record<string, Record<string, number>> = {};
    
    // Initialize structures
    days.forEach(day => {
      grid[day] = {};
      for (let hour = 0; hour < 24; hour++) {
        grid[day][hour] = {};
        uniqueDeviceIds.forEach(deviceId => {
          grid[day][hour][deviceId] = 0;
        });
      }
    });
    
    for (let hour = 0; hour < 24; hour++) {
      hourlyByDevice[hour] = {};
      uniqueDeviceIds.forEach(deviceId => {
        hourlyByDevice[hour][deviceId] = 0;
      });
    }
    
    days.forEach(day => {
      weeklyByDevice[day] = {};
      uniqueDeviceIds.forEach(deviceId => {
        weeklyByDevice[day][deviceId] = 0;
      });
    });

    // Populate grid with data
    dataPoints.forEach(point => {
      const date = new Date(point.timestamp);
      const dayName = days[date.getDay()];
      const hour = date.getHours();
      const deviceId = point.deviceId || point.studentId || 'Unknown';
      
      if (grid[dayName] && grid[dayName][hour]) {
        grid[dayName][hour][deviceId] = (grid[dayName][hour][deviceId] || 0) + 1;
      }
      
      if (hourlyByDevice[hour]) {
        hourlyByDevice[hour][deviceId] = (hourlyByDevice[hour][deviceId] || 0) + 1;
      }
      
      if (weeklyByDevice[dayName]) {
        weeklyByDevice[dayName][deviceId] = (weeklyByDevice[dayName][deviceId] || 0) + 1;
      }
    });

    // Find max value for color scaling
    let maxValue = 0;
    days.forEach(day => {
      for (let hour = 0; hour < 24; hour++) {
        const total = Object.values(grid[day][hour]).reduce((sum, val) => sum + val, 0);
        maxValue = Math.max(maxValue, total);
      }
    });

    // Convert grid to flat cells array for rendering
    const cells: { day: string; dayIndex: number; hour: number; total: number; byDevice: Record<string, number> }[] = [];
    days.forEach((day, dayIndex) => {
      for (let hour = 0; hour < 24; hour++) {
        const byDevice = grid[day][hour];
        const total = Object.values(byDevice).reduce((sum, val) => sum + val, 0);
        cells.push({ day, dayIndex, hour, total, byDevice });
      }
    });

    // Convert hourly data for bar chart
    const hourlyData = Object.entries(hourlyByDevice).map(([hour, devices]) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      hourNum: parseInt(hour),
      ...devices
    }));

    // Convert weekly data for bar chart
    const weeklyData = days.map(day => ({
      day,
      ...weeklyByDevice[day]
    }));

    return { cells, maxValue, hourlyData, weeklyData };
  }, [dataPoints, uniqueDeviceIds]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const uniqueStudents = new Set(dataPoints.map(p => p.studentId));
    const timeRange = dataPoints.length > 0 ? {
      start: new Date(Math.min(...dataPoints.map(p => new Date(p.timestamp).getTime()))),
      end: new Date(Math.max(...dataPoints.map(p => new Date(p.timestamp).getTime()))),
    } : null;
    
    // Find peak hour
    let peakHour = '00:00';
    let peakCount = 0;
    activityHeatmapData.hourlyData.forEach(h => {
      const total = uniqueDeviceIds.reduce((sum, id) => sum + ((h as any)[id] || 0), 0);
      if (total > peakCount) {
        peakCount = total;
        peakHour = h.hour;
      }
    });
    
    // Calculate average per day
    const daysWithData = new Set(dataPoints.map(p => new Date(p.timestamp).toDateString())).size;
    const avgPerDay = daysWithData > 0 ? Math.round(dataPoints.length / daysWithData) : 0;

    return {
      totalPoints: dataPoints.length,
      uniqueStudents: uniqueStudents.size,
      totalDwells: dwellLocations.length,
      totalCoTravelers: coTravelerResults.length,
      timeRange,
      peakHour,
      avgPerDay,
    };
  }, [dataPoints, dwellLocations, coTravelerResults, activityHeatmapData.hourlyData, uniqueDeviceIds]);

  if (stats.totalPoints === 0 && stats.totalDwells === 0 && stats.totalCoTravelers === 0) {
    return (
      <Box sx={{ p: 1.5, textAlign: 'center' }}>
        <TimelineIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          No Data Loaded
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Load device data to see timeline visualizations
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
      {/* Compact Summary Statistics */}
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1, py: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
            {stats.totalPoints.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Points</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography sx={{ color: 'secondary.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
            {stats.totalDwells}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Dwells</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
            {stats.peakHour}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Peak</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
            {stats.avgPerDay.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Avg/Day</Typography>
        </Box>
      </Box>

      {/* Compact Tabs for ACTIVITY / LOCATIONS with Export Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ 
            flex: 1,
            minHeight: 32,
            '& .MuiTab-root': { 
              fontWeight: 'bold',
              fontSize: '0.7rem',
              minHeight: 32,
              py: 0.5,
            }
          }}
        >
          <Tab icon={<CalendarIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="ACTIVITY" />
          <Tab icon={<PlaceIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="LOCATIONS" />
        </Tabs>
        
        {/* Export Button */}
        <MuiTooltip title="Export Charts">
          <IconButton
            onClick={handleExportMenuOpen}
            disabled={isExporting}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              borderRadius: 1,
              px: 1,
            }}
          >
            {isExporting ? <CircularProgress size={16} color="inherit" /> : <ExportIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </MuiTooltip>
        
        {/* Export Menu */}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              minWidth: 220,
              '& .MuiMenuItem-root': { fontSize: '0.85rem', py: 0.75 },
            }
          }}
        >
          <Typography 
            variant="overline" 
            sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
          >
            Export Data (CSV)
          </Typography>
          <MenuItem onClick={handleExportAllDataCSV}>
            <ListItemIcon><DataIcon fontSize="small" /></ListItemIcon>
            <ListItemText>All Chart Data</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportHeatmapCSV} disabled={activityHeatmapData.cells.length === 0}>
            <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Activity Heatmap</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportHourlyCSV} disabled={activityHeatmapData.hourlyData.length === 0}>
            <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Hourly Activity</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportWeeklyCSV} disabled={activityHeatmapData.weeklyData.length === 0}>
            <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Weekly Pattern</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportDwellCSV} disabled={dwellTimelineData.length === 0}>
            <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Dwell Locations</ListItemText>
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          <Typography 
            variant="overline" 
            sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
          >
            Export Images
          </Typography>
          <MenuItem onClick={handleExportCurrentTabPNG}>
            <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Current Tab as PNG</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportCurrentTabPDF}>
            <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Current Tab as PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportFullPDFReport}>
            <ListItemIcon><PdfIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primary="Full PDF Report" secondary="All charts" />
          </MenuItem>
        </Menu>
      </Box>

      {/* Active Filter Indicator */}
      {activeFilter && (
        <Alert 
          severity="info" 
          icon={<FilterIcon />}
          action={
            <IconButton size="small" onClick={clearFilter} sx={{ color: 'inherit' }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          }
          sx={{ mb: 1.5, py: 0.5 }}
        >
          <Typography variant="body2">
            Filtering: {activeFilter.type === 'hour' && `Hour ${activeFilter.hour?.toString().padStart(2, '0')}:00`}
            {activeFilter.type === 'day' && `${activeFilter.day}`}
            {activeFilter.type === 'hourDay' && `${activeFilter.day} ${activeFilter.hour?.toString().padStart(2, '0')}:00`}
            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
              (click again to clear)
            </Typography>
          </Typography>
        </Alert>
      )}

      {/* ACTIVITY Tab Content */}
      {activeTab === 0 && (
        <Box ref={activityChartsRef}>
          {/* Activity Heatmap (Hour × Day of Week) */}
          {activityHeatmapData.cells.length > 0 && (
            <Paper ref={heatmapRef} sx={{ p: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 16 }} /> Activity Heatmap
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Hour × Day of Week
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Less</Typography>
                  {[0.2, 0.4, 0.6, 0.8, 1].map((opacity, i) => (
                    <Box 
                      key={i}
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        bgcolor: 'info.main',
                        opacity,
                        borderRadius: 0.5
                      }} 
                    />
                  ))}
                  <Typography variant="caption" color="text.secondary">More</Typography>
                </Box>
              </Box>
              
              {/* Hour labels */}
              <Box sx={{ display: 'flex', pl: 5, mb: 0.5 }}>
                {[0, 3, 6, 9, 12, 15, 18, 21].map(hour => (
                  <Typography 
                    key={hour} 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ width: `${100/8}%`, textAlign: 'center', fontSize: '0.6rem' }}
                  >
                    {hour.toString().padStart(2, '0')}
                  </Typography>
                ))}
              </Box>
              
              {/* Heatmap Grid */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIndex) => (
                <Box key={day} sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ width: 40, fontSize: '0.7rem', fontWeight: 500 }}
                  >
                    {day}
                  </Typography>
                  <Box sx={{ display: 'flex', flex: 1, gap: '2px' }}>
                    {Array.from({ length: 24 }, (_, hour) => {
                      const cell = activityHeatmapData.cells.find(c => c.dayIndex === dayIndex && c.hour === hour);
                      const total = cell?.total || 0;
                      const maxVal = activityHeatmapData.maxValue || 1;
                      const intensity = maxVal > 0 ? total / maxVal : 0;
                      
                      // Determine dominant device for color
                      let dominantColor = theme.palette.info.main;
                      if (cell?.byDevice && total > 0) {
                        let maxDeviceCount = 0;
                        Object.entries(cell.byDevice).forEach(([deviceId, count]) => {
                          if (count > maxDeviceCount) {
                            maxDeviceCount = count;
                            dominantColor = generateDeviceColor(deviceId);
                          }
                        });
                      }
                      
                      const isSelected = activeFilter?.type === 'hourDay' && 
                                         activeFilter.hour === hour && 
                                         activeFilter.dayIndex === dayIndex;
                      
                      return (
                        <MuiTooltip 
                          key={`${day}-${hour}`}
                          title={`${day} ${hour}:00 - ${total} points (click to filter)`}
                          arrow
                          placement="top"
                        >
                          <Box
                            onClick={() => handleHeatmapCellClick(hour, day, dayIndex)}
                            sx={{
                              flex: 1,
                              height: 14,
                              bgcolor: total > 0 ? dominantColor : theme.palette.action.hover,
                              opacity: total > 0 ? Math.max(0.2, intensity) : 0.3,
                              borderRadius: 0.5,
                              cursor: 'pointer',
                              transition: 'all 0.1s',
                              border: isSelected ? '2px solid' : 'none',
                              borderColor: isSelected ? 'warning.main' : 'transparent',
                              transform: isSelected ? 'scale(1.3)' : 'none',
                              zIndex: isSelected ? 2 : 'auto',
                              '&:hover': {
                                transform: 'scale(1.2)',
                                zIndex: 1,
                              }
                            }}
                          />
                        </MuiTooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Hourly Activity Bar Chart */}
          {activityHeatmapData.hourlyData.length > 0 && (
            <Paper ref={hourlyChartRef} sx={{ p: 1.5, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 16 }} /> Hourly Activity
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (click to filter)
                </Typography>
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart 
                  data={activityHeatmapData.hourlyData}
                  onClick={(data) => {
                    if (data?.activePayload?.[0]?.payload?.hourNum !== undefined) {
                      handleHourClick(data.activePayload[0].payload.hourNum);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                    interval={2}
                  />
                  <YAxis tick={{ fontSize: 9, fill: theme.palette.text.secondary }} stroke={theme.palette.divider} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper, 
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                    }}
                    itemStyle={{ fontSize: '0.65rem', padding: 0 }}
                    labelStyle={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: 2 }}
                    wrapperStyle={{ zIndex: 1000 }}
                    cursor={{ fill: 'rgba(100, 116, 139, 0.2)' }}
                  />
                  {uniqueDeviceIds.map((deviceId) => (
                    <Bar 
                      key={deviceId}
                      dataKey={deviceId} 
                      stackId="devices"
                      fill={generateDeviceColor(deviceId)} 
                      name={deviceId.length > 15 ? deviceId.slice(0, 15) + '...' : deviceId}
                    >
                      {activityHeatmapData.hourlyData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`}
                          opacity={activeFilter?.type === 'hour' && activeFilter.hour === entry.hourNum ? 1 : 
                                   activeFilter?.type === 'hour' ? 0.3 : 0.85}
                          stroke={activeFilter?.type === 'hour' && activeFilter.hour === entry.hourNum ? theme.palette.warning.main : 'none'}
                          strokeWidth={activeFilter?.type === 'hour' && activeFilter.hour === entry.hourNum ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Weekly Pattern Bar Chart */}
          {activityHeatmapData.weeklyData.length > 0 && (
            <Paper ref={weeklyChartRef} sx={{ p: 1.5, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <BarChartIcon sx={{ fontSize: 16 }} /> Weekly Pattern
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (click to filter)
                </Typography>
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart 
                  data={activityHeatmapData.weeklyData}
                  onClick={(data) => {
                    if (data?.activePayload?.[0]?.payload?.day) {
                      const day = data.activePayload[0].payload.day;
                      const dayIndex = days.indexOf(day);
                      handleDayClick(day, dayIndex);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis tick={{ fontSize: 9, fill: theme.palette.text.secondary }} stroke={theme.palette.divider} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper, 
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                    }}
                    itemStyle={{ fontSize: '0.65rem', padding: 0 }}
                    labelStyle={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: 2 }}
                    wrapperStyle={{ zIndex: 1000 }}
                    cursor={{ fill: 'rgba(100, 116, 139, 0.2)' }}
                  />
                  {uniqueDeviceIds.map((deviceId) => (
                    <Bar 
                      key={deviceId}
                      dataKey={deviceId} 
                      stackId="devices"
                      fill={generateDeviceColor(deviceId)} 
                      name={deviceId.length > 15 ? deviceId.slice(0, 15) + '...' : deviceId}
                    >
                      {activityHeatmapData.weeklyData.map((entry, index) => {
                        const entryDayIndex = days.indexOf(entry.day);
                        return (
                          <Cell 
                            key={`cell-${index}`}
                            opacity={activeFilter?.type === 'day' && activeFilter.dayIndex === entryDayIndex ? 1 : 
                                     activeFilter?.type === 'day' ? 0.3 : 0.85}
                            stroke={activeFilter?.type === 'day' && activeFilter.dayIndex === entryDayIndex ? theme.palette.warning.main : 'none'}
                            strokeWidth={activeFilter?.type === 'day' && activeFilter.dayIndex === entryDayIndex ? 2 : 0}
                          />
                        );
                      })}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Track Activity Timeline */}
      {trackTimelineData.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
            Track Activity Timeline
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.65rem' }}>
                GPS points recorded over time (stacked by device) - click bar to filter by hour
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart 
              data={trackTimelineData}
              onClick={(data) => {
                if (data?.activePayload?.[0]?.payload?.timestamp) {
                  const date = new Date(data.activePayload[0].payload.timestamp);
                  handleHourClick(date.getHours());
                }
              }}
              style={{ cursor: 'pointer' }}
            >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="time" 
                angle={-45}
                textAnchor="end"
                height={80}
                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} stroke={theme.palette.divider} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [value, name.length > 12 ? name.slice(0, 12) + '...' : name]}
                    labelStyle={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: 2 }}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper, 
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                    }}
                    itemStyle={{ fontSize: '0.65rem', padding: 0 }}
                    wrapperStyle={{ zIndex: 1000 }}
                    cursor={{ fill: 'rgba(100, 116, 139, 0.2)' }}
                  />
                  {uniqueDeviceIds.map((deviceId) => (
                    <Bar 
                      key={deviceId}
                      dataKey={deviceId} 
                      stackId="devices"
                      fill={generateDeviceColor(deviceId)} 
                      name={deviceId}
                    />
                  ))}
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Co-Traveler Timeline */}
      {coTravelerTimelineData.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
            👥 Co-Traveler Encounters Timeline
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.65rem' }}>
            Proximity encounters detected over time
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={coTravelerTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="time" 
                angle={-45}
                textAnchor="end"
                height={80}
                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} stroke={theme.palette.divider} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper, 
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                    }}
                    itemStyle={{ fontSize: '0.65rem', padding: 0 }}
                    labelStyle={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: 2 }}
                    wrapperStyle={{ zIndex: 1000 }}
                    cursor={{ fill: 'rgba(100, 116, 139, 0.2)' }}
                  />
              <Line 
                type="monotone" 
                dataKey="encounters" 
                stroke={theme.palette.success.main} 
                name="Encounters"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="pairs" 
                stroke="#ff5722" 
                name="Device Pairs"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
          )}
        </Box>
      )}

      {/* LOCATIONS Tab Content */}
      {activeTab === 1 && (
        <Box ref={locationsChartsRef}>
          {/* Place Names Toggle */}
          {setEnablePlaceNames && (
            <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlaceNameIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Place Names:
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isEnrichingSites && <CircularProgress size={16} />}
                  <ToggleButtonGroup
                    value={enablePlaceNames ? 'on' : 'off'}
                    exclusive
                    onChange={(_, val) => val && setEnablePlaceNames(val === 'on')}
                    size="small"
                    sx={{ height: 28 }}
                  >
                    <ToggleButton 
                      value="off" 
                      sx={{ 
                        px: 1.5, 
                        py: 0.25, 
                        fontSize: '0.75rem',
                        fontWeight: !enablePlaceNames ? 'bold' : 'normal'
                      }}
                    >
                      OFF
                    </ToggleButton>
                    <ToggleButton 
                      value="on" 
                      sx={{ 
                        px: 1.5, 
                        py: 0.25, 
                        fontSize: '0.75rem',
                        fontWeight: enablePlaceNames ? 'bold' : 'normal'
                      }}
                    >
                      ON
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Dwell Data Timeline */}
          {dwellTimelineData.length > 0 ? (
            <Paper ref={dwellChartRef} sx={{ p: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PlaceIcon sx={{ fontSize: 16 }} /> Dwell Duration Analysis
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Pagination controls */}
                  {totalDwellPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => setDwellPage(p => Math.max(0, p - 1))}
                        disabled={dwellPage === 0}
                        sx={{ p: 0.25 }}
                      >
                        <PrevIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Typography variant="caption" sx={{ mx: 0.5, minWidth: 45, textAlign: 'center' }}>
                        {dwellPage + 1}/{totalDwellPages}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setDwellPage(p => Math.min(totalDwellPages - 1, p + 1))}
                        disabled={dwellPage >= totalDwellPages - 1}
                        sx={{ p: 0.25 }}
                      >
                        <NextIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                  <MuiTooltip title="Show more/less dwells">
                    <IconButton 
                      size="small" 
                      onClick={(e) => setDwellLimitAnchor(e.currentTarget)}
                      sx={{ 
                        bgcolor: 'action.hover', 
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                    >
                      <ListIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                        Top {dwellLimit}
                      </Typography>
                    </IconButton>
                  </MuiTooltip>
                </Box>
                <Menu
                  anchorEl={dwellLimitAnchor}
                  open={Boolean(dwellLimitAnchor)}
                  onClose={() => setDwellLimitAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {[25, 50, 75, 100].map((limit) => (
                    <MenuItem 
                      key={limit}
                      selected={dwellLimit === limit}
                      onClick={() => {
                        setDwellLimit(limit);
                        setDwellLimitAnchor(null);
                      }}
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      Top {limit}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.65rem' }}>
                Total time at each location (grouped) - click to zoom
              </Typography>
              <ResponsiveContainer width="100%" height={Math.min(450, paginatedDwellData.length * 28 + 40)}>
                <BarChart 
                  data={paginatedDwellData} 
                  layout="vertical"
                  onClick={(data) => {
                    const payload = data?.activePayload?.[0]?.payload;
                    if (payload?.latitude && payload?.longitude && onDwellClick) {
                      onDwellClick(payload.latitude, payload.longitude, payload.location);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} stroke={theme.palette.divider} />
                  <YAxis 
                    type="category" 
                    dataKey="location" 
                    width={150}
                    tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper, 
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                    }}
                    itemStyle={{ fontSize: '0.65rem', padding: 0 }}
                    labelStyle={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: 2 }}
                    wrapperStyle={{ zIndex: 1000 }}
                    cursor={{ fill: 'rgba(100, 116, 139, 0.2)' }}
                    formatter={(value: number, name: string) => {
                      // Show device-specific duration
                      const deviceName = name.length > 12 ? name.slice(0, 12) + '...' : name;
                      return [`${value} min`, deviceName];
                    }}
                  />
                  {/* Stacked bars - one per device */}
                  {dwellDeviceIds.map((deviceId, idx) => (
                    <Bar 
                      key={deviceId}
                      dataKey={deviceId} 
                      stackId="location"
                      fill={studentColors[deviceId] || generateDeviceColor(deviceId)} 
                      name={deviceId.length > 15 ? deviceId.slice(0, 15) + '...' : deviceId}
                    >
                      {/* Add visit label only on the last (rightmost) bar segment */}
                      {idx === dwellDeviceIds.length - 1 && (
                        <LabelList 
                          dataKey="visitLabel" 
                          position="right" 
                          fill={theme.palette.text.secondary}
                          fontSize={9}
                          fontWeight="bold"
                        />
                      )}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <PlaceIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No dwell locations detected. Run PoL Analysis to identify dwell locations.
              </Typography>
            </Paper>
          )}
        </Box>
      )}
      
      {/* Export Snackbar */}
      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setExportSnackbar(prev => ({ ...prev, open: false }))}
        message={exportSnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: exportSnackbar.severity === 'success' ? 'success.main' : 'error.main',
          }
        }}
      />
    </Box>
  );
};

export default InteractiveCharts;
